import { customEvents } from '../config/custom-events';
import cHamburger from '../components/c-hamburger';
import fadeNonActiveLinks from '../utils/fadeNonActiveLinks';
import throttle from '../utils/throttle';
import { actionKeyCodes } from '../config/action-key-codes';
import { config } from './nav/config';

export default function header() {
  /**
   * Header wrapper selector
   * @type {Element|null}
   */
  const headerWrappers = document.querySelectorAll('.js-header-wrap');

  /**
   * Header primary wrapper selector
   * @type {Element|null}
   */
  const headerPrimaryWrap = document.querySelector('.js-header-wrap-primary');

  /**
   * Common navigation toggle selector (destination/secondary)
   * @type {string}
   */
  const commonNavigationToggleSelector = '.js-header-nav-toggle';

  /**
   * Secondary navigation toggles selector
   * @type {string}
   */
  const secondaryNavigationTogglesSelector = '.js-header-secondary-navigation-toggle';

  /**
   * Destination navigation toggles selector
   * @type {Element|null}
   */
  const destinationNavigationToggleSelector = document.querySelector('.js-header-destinations-arrow');

  /**
   * Destination navigation toggles selector (for keyboard navigation)
   * @type {NodeListOf<Element>}
   */
  const destinationTabNavigationToggles = document.querySelectorAll('.js-header-tab-destination-navigation-toggle');

  /**
   * Secondary navigation toggles selector (for keyboard navigation)
   * @type {NodeListOf<Element>}
   */
  const secondaryTabNavigationToggles = document.querySelectorAll('.js-header-tab-navigation-toggle');

  /**
   * Header init modifier class
   * @type {string}
   */
  const headerWrapInitModifierClass = 'header__wrap--state-init';

  /**
   * Header dropdown btn modifier class
   * @type {Element|null}
   */
  const headeDropdownBtnElemenets = document.querySelectorAll('.header__booking-btn--dropdown');

  /**
   * Header mobile booking dropdown
   * @type {Element|null}
   */
  const headerDropdownBookingMobile = document?.querySelector('.header__booking-dropdown--mobile');

  /**
   * Header hidden modifier class
   * @type {string}
   */
  const headerWrapHiddenModifierClass = 'header__wrap--state-hidden';

  /**
   * Scroll position from top
   * @type {number}
   */
  let prevScrollPos = window.pageYOffset || document.documentElement.scrollTop;

  /**
   * is header hidden flag
   * @type {boolean}
   */
  let isHeaderHidden = false;

  /**
   * Prevent header from hiding
   * Can be changed by external custom events
   * @type {boolean}
   */
  let preventHeaderFromHiding = false;

  /**
   * Is destinations menu open flag
   * @type {boolean}
   */
  let isDestinationsMenuOpened = false;

  /**
   * Initial width holder
   * @type {number}
   */
  let width = window.innerWidth;

  // init hamburger
  cHamburger(customEvents.navigation.main.open, customEvents.navigation.main.close);

  // init links hover effect
  ['.js-header-primary-navigation-link', '.js-header-secondary-navigation-link'].forEach((selector) => {
    fadeNonActiveLinks(selector, 'header__navigation-link--state-faded');
  });

  /**
   * Show header
   * @param {boolean} init is first show on DOM ready
   * @return {void}
   */
  function showHeader(init = false) {
    headerPrimaryWrap.setAttribute('aria-hidden', 'false');
    headerWrappers.forEach((el) => {
      el.classList.remove(init ? headerWrapInitModifierClass : headerWrapHiddenModifierClass);
    });
  }

  /**
   * Handle Booking Dropdown close event
   * @return {void}
   */
  function closeBookingDropdowns() {
    headeDropdownBtnElemenets.forEach((btn) => {
      if (btn.classList.contains('open')) {
        btn.classList.remove('open');

        headerDropdownBookingMobile.classList?.remove('open');
        headerDropdownBookingMobile?.removeAttribute('style');
        btn.nextElementSibling?.classList.remove('open');
      }
    });
  }

  /**
   * Handle Dropdown toggle
   * @return {void}
   */
  const toggleDropdown = (btn) => {
    const isOpen = btn.classList.toggle('open');
    const dropdown = btn.nextElementSibling;
    dropdown.classList.toggle('open', isOpen);
  };

  /**
   * Hide header
   * @return {void}
   */
  function hideHeader() {
    if (!preventHeaderFromHiding) {
      headerPrimaryWrap.setAttribute('aria-hidden', 'true');
      headerWrappers.forEach((el) => {
        el.classList.add(headerWrapHiddenModifierClass);
      });
    }
  }

  /**
   * Handle scroll event:
   * Detect scroll direction and hide header
   * Optimized with throttle and fires only if direction changes
   * @return {void}
   */
  function handleScroll() {
    const currentScrollPos = window.pageYOffset || document.documentElement.scrollTop;
    const scrollDifference = Math.abs(prevScrollPos - currentScrollPos);

    // prevent hiding header on bottom of the page (safari ios)
    if (currentScrollPos > (document.documentElement.scrollHeight - window.innerHeight)) {
      return;
    }

    if (scrollDifference >= 5) {
      if (currentScrollPos < prevScrollPos && isHeaderHidden) {
        isHeaderHidden = false;
        showHeader();
      } else if (currentScrollPos > prevScrollPos && !isHeaderHidden && currentScrollPos > 30) {
        isHeaderHidden = true;
        hideHeader();
      }
    }

    prevScrollPos = currentScrollPos <= 0 ? 0 : currentScrollPos;
    closeBookingDropdowns();
  }

  /**
   * Handle DOM ready event
   * Show header on DOM ready / remove initial inline css properties
   * @return {void}
   */
  function handleDomReady() {
    showHeader(true);
  }

  /**
   * Handle navigation open event
   * @return {void}
   */
  function handleNavigationOpen() {
    preventHeaderFromHiding = true;
    isDestinationsMenuOpened = false;
    document.dispatchEvent(new CustomEvent(customEvents.navigation.destinations.close));
  }

  /**
   * Handle resize for destinations menu
   */
  function handleResize() {
    if (window.innerWidth !== width) {
      width = window.innerWidth;

      if (isDestinationsMenuOpened) {
        document.dispatchEvent(new CustomEvent(customEvents.navigation.destinations.close));
      }
    }

    closeBookingDropdowns();
  }

  /**
   * Orientation change handler
   */
  function handleOrientationChange() {
    if (isDestinationsMenuOpened) {
      document.dispatchEvent(new CustomEvent(customEvents.navigation.destinations.close));
    }
  }

  /**
   * Handle navigation close event
   * @return {void}
   */
  function handleNavigationClose() {
    preventHeaderFromHiding = false;
  }

  /**
   * Handle navigation toggle event
   * @param {Element} el
   * @param {string} openEvt
   * @param {string} closeEvt
   */
  function handleKeyboardNavigationToggle(el, openEvt, closeEvt) {
    // eslint-disable-next-line max-len
    const toggle = el.classList.contains(commonNavigationToggleSelector) ? el : el.closest(commonNavigationToggleSelector);
    const isOpened = toggle.getAttribute('aria-expanded') === 'true';

    if (isOpened) {
      return document.dispatchEvent(new CustomEvent(closeEvt, {
        detail: { toggle, isKeyboardNavigation: true },
      }));
    }

    return document.dispatchEvent(new CustomEvent(openEvt, {
      detail: { toggle, isKeyboardNavigation: true },
    }));
  }

  /**
   * Remove initial inline css properties after transition
   */
  headerWrappers.forEach((el) => {
    el.addEventListener('transitionend', (event) => {
      event.target.classList.remove(headerWrapInitModifierClass);
    }, { once: true });
  });

  /**
   * Add event listeners
   */
  window.addEventListener('scroll', throttle(handleScroll, 10), false);
  document.addEventListener('DOMContentLoaded', handleDomReady, false);
  document.addEventListener(customEvents.navigation.main.open, handleNavigationOpen);
  document.addEventListener(customEvents.navigation.main.close, handleNavigationClose);

  /**
   * Secondary navigation open/close events from header
   */
  // hover events
  document.querySelectorAll(secondaryNavigationTogglesSelector).forEach((toggle) => {
    toggle.addEventListener('mouseenter', () => {
      document.dispatchEvent(new CustomEvent(customEvents.navigation.secondary.open, {
        detail: { toggle },
      }));
    });

    toggle.addEventListener('mouseleave', () => {
      document.dispatchEvent(new CustomEvent(customEvents.navigation.secondary.close, {
        detail: { toggle },
      }));
    });
  });

  /**
   * Destination navigation open/close events from header
   */
  if (destinationNavigationToggleSelector) {
    destinationNavigationToggleSelector.addEventListener('click', () => {
      if (isDestinationsMenuOpened) {
        document.dispatchEvent(new CustomEvent(customEvents.navigation.destinations.close));
        isDestinationsMenuOpened = false;
      } else {
        isDestinationsMenuOpened = true;
        document.dispatchEvent(new CustomEvent(customEvents.navigation.destinations.open));
      }
    });

    destinationTabNavigationToggles.forEach((arrow) => handleKeyboardNavigationToggle(
      arrow,
      customEvents.navigation.destinations.open,
      customEvents.navigation.destinations.close,
    ));
  }

  if (headeDropdownBtnElemenets) {
    headeDropdownBtnElemenets.forEach((btn) => {
      btn.addEventListener('click', (e) => toggleDropdown(e.currentTarget));
    });
  }

  /*
   * "Click outside" events
   */
  document.querySelectorAll(`${config.main}, ${config.footer}`).forEach((el) => {
    el.addEventListener('click', () => {
      if (document.querySelector(config.darkOverlaySelector)) {
        document.dispatchEvent(new CustomEvent(customEvents.navigation.destinations.close));
        isDestinationsMenuOpened = false;
        document.dispatchEvent(new Event(customEvents.navigation.main.close));
      }
    });
  });

  // keyboard events
  secondaryTabNavigationToggles.forEach((arrow) => {
    ['click', 'tap'].forEach((event) => {
      arrow.addEventListener(event, (evt) => {
        if (!actionKeyCodes.includes(evt.keyCode)) {
          return false;
        }

        return handleKeyboardNavigationToggle(
          arrow,
          customEvents.navigation.secondary.open,
          customEvents.navigation.secondary.close,
        );
      });
    });
  });

  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', handleOrientationChange);
}
